import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { copyDevicesFromASpaceToAnotherSpace, IDevice } from "@multiconsult-group/multiplacer-core";
import { IAppThunk } from "../../../../app/store";
import { selectActivePartialMcbimSpace } from "../../../../McBim/mcbimSelectors";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "../../../designSelector";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  removeNonPublischedDevicesFromAlgosInRedux,
  thunkToast,
} from "../../../designSlice";
import { selectHttpDevicesInRoom } from "../../deviceSlice";
import { withWorldZInCoordinate } from "../../deviceUtils";
import {
  selectExistingDevicesInSelectedTargetRoomForAdvancedCopy,
  selectSelectedTargetMcbimSpaceForAdvancedCopy,
  selectSelectedTargetRoomForAdvancedCopy,
} from "./advancedCopyDevicesSelectors";

export const thunkCopyDevicesToSingleSelectedTargetRoom = (): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const sourceSpace = selectActivePartialMcbimSpace(state);
    const targetSpace = selectSelectedTargetMcbimSpaceForAdvancedCopy(state);
    const targetRoom = selectSelectedTargetRoomForAdvancedCopy(state);
    const sourceDevices = selectHttpDevicesInRoom(state);
    const existingTargetDevices = selectExistingDevicesInSelectedTargetRoomForAdvancedCopy(state);
    const nonPublishedDevices = state.design.nonPublischedDevicesFromAlgos;

    if (!sourceSpace || !targetSpace) {
      dispatch(
        thunkToast({
          text: "Kilde- eller målrom mangler.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    if (sourceSpace.name === targetSpace.name) {
      return;
    }

    if (existingTargetDevices.length > 0) {
      dispatch(
        thunkToast({
          text: "Det finnes allerede komponenter i målrommet.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    const existingNonPublishedInTarget = nonPublishedDevices.filter(
      (device) => device.roomNumber === targetSpace.name
    );

    if (existingNonPublishedInTarget.length > 0) {
      dispatch(
        removeNonPublischedDevicesFromAlgosInRedux(
          existingNonPublishedInTarget.map((device) => String(device._id))
        )
      );
    }

    const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
      devicesToCopy: sourceDevices as IDevice[],
      spaceToCopyFrom: sourceSpace,
      spaceToCopyTo: targetSpace,
      roomToCopyTo: (targetRoom ?? undefined) as IRoomSummary | undefined,
    });

    if (copiedDevices.length === 0) {
      dispatch(
        thunkToast({
          text: `Ingen komponenter kunne kopieres fordi målrommet ${targetSpace.name} skiller seg for mye fra kilderommet ${sourceSpace.name}.`,
          header: "Advarsel",
          isSuccess: false,
        })
      );
      return;
    }

    const activeFloorElevationM = state.design.floorAndJob?.elevation;
    const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(state);
    const buildingStory = buildingStories.find(
      (story) => story.name === targetSpace.buildingStory?.name
    );
    const heightFromFloor = getHeightFromFirstCeilingGrid(targetSpace.ceilingGrids ?? []) ?? 2700;
    const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

    const enrichedCopiedDevices = copiedDevices.map((device) => ({
      ...device,
      floor: targetSpace.buildingStory?.name ?? device.floor,
      roomName: targetSpace.longName ?? targetRoom?.roomName ?? device.roomName,
      bottomEdgeHeightAboveFloorMm: heightFromFloor,
      coordinate: withWorldZInCoordinate(device.coordinate, floorElevationM, heightFromFloor),
    }));

    dispatch(addNonPublischedDevicesFromAlgosInRedux(enrichedCopiedDevices));
  };
};
